import * as React from 'react';
import { storeDispatch } from '../state/Store';
import styled, { css } from 'styled-components';
import SmallButton from './components/SmallButton';
import { generateClassName, styledWithProps } from '../utils';
import Layer from '../data/Layer';
import Tooltip from './components/ButtonTooltip';
import { SignalSource } from '../data/SignalSource';
import { SignalVerticalBar } from './SignalVerticalBar';
import { Effect } from '@beatflyer/shaders-lib';
import { CompositionEffect } from 'compose/CompositionEffect';
import { ToggleEffectVisibilityCommand } from 'state/commands/ToggleEffectVisibilityCommand';

interface VisibilityButtonProps {
  hide: Boolean;
}
const VisibilityButton = styledWithProps<VisibilityButtonProps>()(
  styled(SmallButton)
)`
  background-color: rgba(var(--controller-fg-rgb), 1);
  mask: url(/media/icons/effect-visibility-on.svg) no-repeat 50% 50%;

  .filter-row--hidden & {
    opacity: 0.3;

    &:hover{
      opacity: 1;
    }
  }
`;

interface SettingsButtonProps {
  editing: boolean;
}
const SettingsButton = styledWithProps<SettingsButtonProps>()(
  styled(SmallButton)
)`
  position: relative;

  .filter-row--hidden & {
    opacity: 0.5;
  }
  margin-left: 6px;
  border-radius: 50%;

  &:after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(var(--controller-fg-rgb), 1);
    mask: url('/media/icons/icon-effect-settings.svg') no-repeat 50% 50%;
  }

  ${props =>
    props.editing &&
    css`
      opacity: 1;
      background-color: var(--menu-bg-color-hover);
    `};
`;

const Title = styled.h4`
  flex: 1;
  font-size: var(--effect-font-size);
  color: rgba(var(--controller-fg-rgb), 1);
  letter-spacing: 0.5px;
  line-height: 18px;

  .filter-row--hidden & {
    opacity: 0.5;
  }

  .dot {
    width: 10px;
    height: 10px;
    display: inline-block;
    border-radius: 5px;
    border: 1px solid var(--menu-divider-color);
  }
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 0 10px 0 3px;
  background-color: var(--controller-bg-color);

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
  }
`;
interface Props {
  layer: Layer;
  value: Effect | CompositionEffect;
  isEditing: boolean;
  onShowSettings: (
    target: HTMLElement,
    effect: Effect | CompositionEffect
  ) => void;
}

export default class extends React.Component<Props> {
  settingsButton = React.createRef<HTMLButtonElement>();
  visibilityButton = React.createRef<HTMLButtonElement>();

  toggleVisibility = () => {
    storeDispatch(
      new ToggleEffectVisibilityCommand(
        this.props.layer.id,
        this.props.value.id
      )
    );
  };

  startChangingSignal = () => {
    this.setState({ isChangingSignal: true });
  };

  showSettings = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!this.props.value.visible) {
      this.toggleVisibility();
    }
    this.props.onShowSettings(e.currentTarget, this.props.value);
  };

  didClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (
      e.target !== this.visibilityButton.current &&
      e.target !== this.settingsButton.current &&
      this.settingsButton.current
    ) {
      this.props.onShowSettings(this.settingsButton.current, this.props.value);
    }
  };

  didChangeSignal = (newSignal: SignalSource) => {
    (this.props.value as Effect).signalSource = newSignal;
    this.forceUpdate();
  };

  render() {
    const filter = this.props.value;
    const filterId = (filter.constructor as any).id as string;
    const isComposition = filter instanceof CompositionEffect;
    return (
      <Wrapper
        className={generateClassName('filter-row', {
          hidden: !this.props.value.visible,
          composition: isComposition
        })}
        onClick={this.didClick}
      >
        <Tooltip
          placement="topLeft"
          text={this.props.value.visible ? 'Hide effect' : 'Show effect'}
        >
          <VisibilityButton
            hide={!this.props.value.visible}
            onClick={this.toggleVisibility}
            innerRef={this.visibilityButton}
          />
        </Tooltip>
        {!isComposition && !(filter as Effect).isSplitEffect && (
          <SignalVerticalBar
            signal={(this.props.value as Effect).signalSource}
            invert={(this.props.value as Effect).isInverted}
          />
        )}
        {isComposition && <div className="composition-icon" />}
        <Title>
          {(filter.constructor as any).label}{' '}
          {filterId === 'colorize' && (
            <span
              className="dot"
              style={{ backgroundColor: filter.getProperty('color') }}
            />
          )}
        </Title>
        <Tooltip text="Settings" hide={this.props.isEditing}>
          <SettingsButton
            onClick={this.showSettings}
            editing={this.props.isEditing}
            innerRef={this.settingsButton}
          />
        </Tooltip>
      </Wrapper>
    );
  }
}



// WEBPACK FOOTER //
// ./src/controller/FilterRow.tsx